import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiSearch, FiDownload } from 'react-icons/fi';
import reportesAvanzadosService from '../services/reportesAvanzadosService';
import Loading from '../components/Loading';
import './RentabilidadCentroPage.css';

const RentabilidadCentroPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const cargar = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (fechaDesde) filters.fecha_desde = fechaDesde;
      if (fechaHasta) filters.fecha_hasta = fechaHasta;
      const res = await reportesAvanzadosService.getRentabilidadCentroCosto(filters);
      setData(res.data);
    } catch { toast.error('Error al cargar reporte de rentabilidad'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);
  const formatPct = (v) => `${(parseFloat(v || 0)).toFixed(1)}%`;

  const exportarExcel = async () => {
    try {
      const XLSX = (await import('xlsx')).default;
      const centros = data?.centros || [];
      const ws = XLSX.utils.json_to_sheet(centros.map(c => ({
        Codigo: c.codigo, 'Centro de Costo': c.nombre, Tipo: c.tipo, Ingresos: c.ingresos,
        Gastos: c.gastos, Resultado: c.resultado, 'Margen %': c.margen,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Rentabilidad');
      XLSX.writeFile(wb, `rentabilidad_centros_costo.xlsx`);
    } catch { toast.error('Error al exportar'); }
  };

  const centros = data?.centros || data?.results || [];
  const totales = data?.totales || {};

  return (
    <div className="rentabilidad-page">
      <div className="page-header"><h1>Rentabilidad por Centro de Costo</h1></div>

      <div className="selector-bar">
        <div className="form-group">
          <label>Desde</label>
          <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Hasta</label>
          <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
        </div>
        <button className="btn-primary" onClick={cargar}><FiSearch /> Consultar</button>
        {data && <button className="btn-export" onClick={exportarExcel}><FiDownload /> Excel</button>}
      </div>

      {loading && <Loading />}

      {!loading && data && (
        <>
          {/* Resumen */}
          <div className="resumen-cards">
            <div className="stat-card" style={{ borderLeftColor: '#059669' }}>
              <div className="stat-label">Total Ingresos</div>
              <div className="stat-value" style={{ color: '#059669' }}>{formatCurrency(totales.ingresos || centros.reduce((s, c) => s + parseFloat(c.ingresos || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#dc2626' }}>
              <div className="stat-label">Total Gastos</div>
              <div className="stat-value" style={{ color: '#dc2626' }}>{formatCurrency(totales.gastos || centros.reduce((s, c) => s + parseFloat(c.gastos || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
              <div className="stat-label">Resultado Global</div>
              <div className="stat-value">{formatCurrency(totales.resultado || centros.reduce((s, c) => s + parseFloat(c.resultado || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#6b7280' }}>
              <div className="stat-label">Centros Activos</div>
              <div className="stat-value">{centros.length}</div>
            </div>
          </div>

          {/* Tabla */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Centro de Costo</th>
                  <th>Tipo</th>
                  <th style={{ textAlign: 'right' }}>Ingresos</th>
                  <th style={{ textAlign: 'right' }}>Gastos</th>
                  <th style={{ textAlign: 'right' }}>Resultado</th>
                  <th style={{ width: 150 }}>Margen</th>
                </tr>
              </thead>
              <tbody>
                {centros.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin datos para el periodo</td></tr>
                ) : centros.map((c, i) => {
                  const resultado = parseFloat(c.resultado || 0);
                  const margen = parseFloat(c.margen || 0);
                  return (
                    <tr key={i}>
                      <td className="mono">{c.codigo}</td>
                      <td><strong>{c.nombre}</strong></td>
                      <td><span className={`badge badge-${c.tipo === 'proyecto' ? 'blue' : c.tipo === 'departamento' ? 'green' : 'gray'}`}>{c.tipo}</span></td>
                      <td className="mono right" style={{ color: '#059669' }}>{formatCurrency(c.ingresos)}</td>
                      <td className="mono right" style={{ color: '#dc2626' }}>{formatCurrency(c.gastos)}</td>
                      <td className="mono right bold" style={{ color: resultado >= 0 ? '#059669' : '#dc2626' }}>{formatCurrency(c.resultado)}</td>
                      <td>
                        <div className="margen-container">
                          <div className="margen-bar" style={{ width: `${Math.min(Math.abs(margen), 100)}%`, background: margen >= 0 ? '#059669' : '#dc2626' }}></div>
                          <span className="margen-label" style={{ color: margen >= 0 ? '#059669' : '#dc2626' }}>{formatPct(margen)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {centros.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="3" style={{ fontWeight: 700 }}>TOTAL</td>
                    <td className="mono right bold" style={{ color: '#059669' }}>{formatCurrency(totales.ingresos || centros.reduce((s, c) => s + parseFloat(c.ingresos || 0), 0))}</td>
                    <td className="mono right bold" style={{ color: '#dc2626' }}>{formatCurrency(totales.gastos || centros.reduce((s, c) => s + parseFloat(c.gastos || 0), 0))}</td>
                    <td className="mono right bold">{formatCurrency(totales.resultado || centros.reduce((s, c) => s + parseFloat(c.resultado || 0), 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default RentabilidadCentroPage;
